import {
  CascaderField,
  DateRangePickerField,
  Form,
  Loading,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useMyOrganizationOptions } from '@/features/authentication/_hooks/me';
import { LocalTagFormSchema } from '@/features/tag/models';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useTagTypeOptions } from '../hooks';

type FormValues = z.infer<typeof LocalTagFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const LocalTagForm: FC<Props> = ({ type, initialValues, onSubmit }) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      ...initialValues,
      nestedSharedOrganizationIds:
        initialValues?.nestedSharedOrganizationIds ?? [],
    },
    resolver: zodResolver(LocalTagFormSchema),
  });
  const myOrganizationOptions = useMyOrganizationOptions();
  const organizationsOptions = useOrganizationOptionsRecursive();

  const { tagTypeOptions } = useTagTypeOptions();

  if (myOrganizationOptions.isPending || organizationsOptions.isPending) {
    return <Loading />;
  }

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="type"
        label={t('scope-tag-type', { scope: t('local-tag') })}
        control={control}
        errors={errors}
        options={tagTypeOptions}
        required
        disabled={type === 'edit'}
      />
      <TextInputField
        name="name"
        autocompleteKey="localTag.name"
        label={t('scope-tag-name', { scope: t('local-tag') })}
        control={control}
        errors={errors}
        required
      />
      <CascaderField
        name="organizationId"
        label={t('owner-organization')}
        control={control}
        errors={errors}
        options={myOrganizationOptions.data}
        allowClear={false}
        required
      />

      <CascaderField
        name="nestedSharedOrganizationIds"
        label={t('viewable-organization')}
        control={control}
        options={organizationsOptions.data}
        loading={organizationsOptions.isPending}
        errors={errors}
        multiple
        showFullPath
      />

      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
